/**
 * common 命名空间字典（i18n 分区）
 *
 * 从原 translations.ts 主字典按 key 首个点分段拆出；本文件内 key 不带命名空间前缀，
 * 调用点写 t('common:key')。字段内容与拆分前逐字节一致。
 */

import type { TranslationDict } from '../types'

export const commonZh: TranslationDict = {

  // ======================== 通用 / Common ========================
  'loading': '加载中...',
  'group': '群聊',
  'user': '用户',
  'load': '加载',
  'error': '出错了',
  'retry': '重试',
  'close': '关闭',
  'download': '下载',
  'loadFailed': '加载失败',
  'downloadInstead': '直接下载',
  'zoomIn': '放大',
  'zoomOut': '缩小',
  'resetZoom': '重置缩放',
  'fullscreen': '全屏',
  'exitFullscreen': '退出全屏',
  'confirm': '确认',
  'cancel': '取消',
  'delete': '删除',
  'save': '保存',
  'edit': '编辑',
  'copy': '复制',
  'copied': '已复制',
  'searching': '搜索中',
  'search': '搜索',
  'create': '创建',
  'back': '返回',
  'contentWidthHint': '拖动调整内容宽度，双击恢复默认',
  'more': '更多',
  'yes': '是',
  'no': '否',
  'prevPage': '上一页',
  'nextPage': '下一页',
  'collapse': '收起',
  'expand': '展开',
  'refresh': '刷新',
  'export': '导出',
  'exporting': '导出中...',
  'saving': '保存中...',
  'set': '设置',
  'moreTriggers': '+{count} 个触发条件',
  'enabled': '开启',
  'disabled': '关闭',
  'connected': '已连接',
  'disconnected': '未连接',
  'none': '无',
  'all': '全部',
  'default': '默认',
  'optional': '可选',
  'noResults': '无结果',
  'to': '至',
  'menu': '菜单',
  'redeemSuccess': '兑换成功',
  'redeemFailed': '兑换失败',
  'grid': '排列',
  'uploadImage': '上传图片',
  'uploading': '上传中...',

  // common
  'apply': '应用',
  'saved': '已保存',
}

export const commonEn: TranslationDict = {
  'loading': 'Loading...',
  'group': 'Group',
  'user': 'User',
  'load': 'Load',
  'error': 'Error',
  'retry': 'Retry',
  'close': 'Close',
  'download': 'Download',
  'loadFailed': 'Failed to load',
  'downloadInstead': 'Download directly',
  'zoomIn': 'Zoom in',
  'zoomOut': 'Zoom out',
  'resetZoom': 'Reset zoom',
  'fullscreen': 'Fullscreen',
  'exitFullscreen': 'Exit fullscreen',
  'confirm': 'Confirm',
  'cancel': 'Cancel',
  'delete': 'Delete',
  'save': 'Save',
  'edit': 'Edit',
  'copy': 'Copy',
  'copied': 'Copied',
  'searching': 'Searching...',
  'search': 'Search',
  'create': 'Create',
  'back': 'Back',
  'contentWidthHint': 'Drag to resize, double-click to reset',
  'more': 'More',
  'yes': 'Yes',
  'no': 'No',
  'prevPage': 'Previous',
  'nextPage': 'Next',
  'collapse': 'Collapse',
  'expand': 'Expand',
  'refresh': 'Refresh',
  'export': 'Export',
  'exporting': 'Exporting...',
  'saving': 'Saving...',
  'set': 'Set',
  'moreTriggers': '+{count} trigger(s)',
  'enabled': 'Enabled',
  'disabled': 'Disabled',
  'connected': 'Connected',
  'disconnected': 'Disconnected',
  'none': 'None',
  'all': 'All',
  'default': 'Default',
  'optional': 'Optional',
  'noResults': 'No results',
  'to': 'to',
  'menu': 'Menu',
  'redeemSuccess': 'Redeem successful',
  'redeemFailed': 'Redeem failed',
  'grid': 'Grid',
  'uploadImage': 'Upload Image',
  'uploading': 'Uploading...',
  'apply': 'Apply',
  'saved': 'Saved',
}

export const commonJa: TranslationDict = {
  'loading': '読み込み中...',
  'group': 'グループ',
  'user': 'ユーザー',
  'load': '読み込む',
  'error': 'エラー',
  'retry': 'リトライ',
  'close': '閉じる',
  'download': 'ダウンロード',
  'loadFailed': '読み込みに失敗しました',
  'downloadInstead': '直接ダウンロード',
  'zoomIn': '拡大',
  'zoomOut': '縮小',
  'resetZoom': 'ズームをリセット',
  'fullscreen': '全画面',
  'exitFullscreen': '全画面を終了',
  'confirm': '確認',
  'cancel': 'キャンセル',
  'delete': '削除',
  'save': '保存',
  'edit': '編集',
  'copy': 'コピー',
  'copied': 'コピーしました',
  'searching': '検索中...',
  'search': '検索',
  'create': '作成',
  'back': '戻る',
  'contentWidthHint': 'ドラッグで幅を調整、ダブルクリックで既定に戻す',
  'more': 'もっと見る',
  'yes': 'はい',
  'no': 'いいえ',
  'prevPage': '前へ',
  'nextPage': '次へ',
  'collapse': '折りたたむ',
  'expand': '展開',
  'refresh': 'リフレッシュ',
  'export': 'エクスポート',
  'exporting': 'エクスポート中...',
  'saving': '保存中...',
  'set': '設定',
  'moreTriggers': '+{count} 件のトリガー',
  'enabled': '有効',
  'disabled': '無効',
  'connected': '接続済み',
  'disconnected': '未接続',
  'none': 'なし',
  'all': 'すべて',
  'default': 'デフォルト',
  'optional': '任意',
  'noResults': '結果なし',
  'to': '〜',
  'menu': 'メニュー',
  'redeemSuccess': '交換に成功しました',
  'redeemFailed': '交換に失敗しました',
  'grid': 'グリッド',
  'uploadImage': '画像をアップロード',
  'uploading': 'アップロード中...',
  'apply': '適用',
  'saved': '保存しました',
}
